<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('VIEW_PFI')"
            label="Add"
            size="md"
            class="d-none"
            @click="goToPFIOnboarding"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <b-dropdown
              v-if="getUserPermission('EXPORT_PFI_ONBOARDING')"
              id="dropdown-1"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              text="Export"
              class="mr-1"
              variant="primary"
            >
              <b-dropdown-item @click="csvExport()">
                CSV
              </b-dropdown-item>
              <b-dropdown-item @click="excelExport()">
                Excel
              </b-dropdown-item>
              <b-dropdown-item @click="pdfExport()">
                PDF
              </b-dropdown-item>
            </b-dropdown>
            <Button
              label="Filter"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              label="Reset Filter"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="refPfiOnboarding"
      :items="pfiOnboardingList"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="tableHeaders"
      primary-key="id"
      show-empty
      empty-text="No records found"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>
      <template #cell(operatingLicenseIssueDate)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>
      <template #cell(operationCommencementDate)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>
      <template #cell(financialInstitutionType)="data">
        {{ addSpaceToString(data.value) }}
      </template>
      <template #cell(shareholdersFund)="data">
        {{ toBalance(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge></template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('ADD_PFI_ONBOARDING')"
              @click="selectPFIOnboardingItems(data.item, $event.target, 'add', 'resume')"
            >
              <feather-icon icon="PlayIcon" />
              <span class="align-middle ml-50">Resume</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      ok-title="Apply"
      no-close-on-backdrop
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      title="Filters"
      modal-footer
      @ok="fetchPfiOnboardingDraft"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.institutionName"
            label="Institution Name"
            placeholder="Enter institution name"
            type="text"
          />
        </b-col>
        <!-- Name -->
        <b-col md="6">
          <InputField
            id="contact-email"
            v-model="filter.email"
            label="Contact Email Address"
            placeholder="Enter contact email address"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="pfi-code"
            v-model="filter.partnerCode"
            label="PFI Code"
            placeholder="Enter PFI code"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="application-age"
            v-model="filter.applicationAgeInDays"
            label="Age of the Application"
            placeholder="Enter application age"
            type="number"
          />
        </b-col>

        <b-col md="6">
          <SelectField
            id="current-stage"
            v-model="filter.currentStage"
            label="Current Stage"
            placeholder="Select Current Stage"
            :options="currentStageOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BBadge,
  BDropdown, BDropdownItem, BSpinner,
} from 'bootstrap-vue'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import Ripple from 'vue-ripple-directive'
import * as FileSaver from 'file-saver'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import getStatusColor from '../../../../helpers/getStatusColor'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import { toDateWithYear } from '../../../../helpers/formatDate'
import Button from '../../../components/button/Button.vue'
import { lmsPermissions } from '../../../../helpers/permissions'
import addSpaceToString from '../../../../helpers/addSpaceToString'
import { toBalance } from '../../../../helpers/formatAmount'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BDropdown,
    BDropdownItem,
    BBadge,
    BSpinner,
    InputField,
    SelectField,
    TablePagination,
    FullScreenSpinner,
    Button,
    TableEntries,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toBalance,
      addSpaceToString,
      toDateWithYear,
      sortBy: 'name',
      isBusy: false,
      showSpinner: false,
      required,
      getStatusColor,
      modalTitle: 'Create PFI Onboarding',
      buttonLabel: 'Save',
      sortDesc: false,

      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'institutionName', sortable: true, label: 'Institution Name' },
        { key: 'institutionEmail', sortable: true, label: 'Institution Email' },
        { key: 'institutionAddress', sortable: true, label: 'Institution Address' },
        { key: 'fullName', sortable: true, label: 'Contact Fullname' },
        { key: 'emailAddress', sortable: true, label: 'Contact Email Address' },
        { key: 'contactPhoneNumber', sortable: true, label: 'Contact Phone Number' },
        { key: 'roleWithinTheOrganization', sortable: true, label: 'Role Within The Organization' },
        { key: 'city', sortable: true, label: 'Institution City' },
        { key: 'state', sortable: true, label: 'Institution State' },
        { key: 'localGovernmentArea', sortable: true, label: 'Institution LGA' },
        { key: 'shareholdersFund', sortable: true, label: 'Shareholder Fund' },
        { key: 'financialInstitutionType', sortable: true, label: 'Institution Type' },
        { key: 'operatingLicenseIssueDate', sortable: true, label: 'License Issue Date' },
        { key: 'operationCommencementDate', sortable: true, label: 'License Commencement Date' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions' },
      ],
      pfiOnboardingList: [],
      currentStageOptions: [
        { value: 'BackgroundInformation', label: 'Background Information' },
        { value: 'ProfitabilityRequirements', label: 'Profitability Requirements' },
        { value: 'RiskManagement', label: 'Risk Management' },
        { value: 'Compliance', label: 'Compliance' },
        { value: 'ConsumerCreditSector', label: 'Consumer Credit Sector' },
      ],

      filter: {
        institutionName: '',
        email: '',
        currentStage: '',
        applicationAgeInDays: '',
      },
    }
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchPfiOnboardingDraft()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchPfiOnboardingDraft()
    },
  },
  mounted() {
    this.fetchPfiOnboardingDraft()
  },
  methods: {
    ...mapActions({
      getPfiOnboardingDraft: 'account/getPfiOnboardingDraftList',
      downloadPfiOnboarding: 'account/downloadPfiOnboarding',
    }),
    goToPFIOnboarding() {
      this.$router.push({
        name: 'pfi-onboarding',
        params: {
          type: 'add',
        },
      }).catch(error => {
        this.alertError({ text: error })
      })
    },
    selectPFIOnboardingItems(item, activity, val, val2) {
      this.$router.push({
        name: 'pfi-onboarding',
        params: {
          type: val,
          cat: val2,
          id: item.id,
          emailAddress: item.emailAddress,
        },
      }).catch(error => {
        this.alertError({ text: error })
      })
    },
    async fetchPfiOnboardingDraft() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getPfiOnboardingDraft({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: lmsPermissions.VIEW_PFI,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.pfiOnboardingList = data.data.map(item => ({
            ...item,
            institutionName: item.backgroundInformation.institutionName,
            institutionEmail: item.backgroundInformation.institutionEmail,
            institutionAddress: item.backgroundInformation.institutionAddress,
            contactPhoneNumber: item.contactInfo?.[0]?.contactPhoneNumber,
            fullName: item.contactInfo?.[0]?.fullName,
            roleWithinTheOrganization: item.contactInfo?.[0]?.roleWithinTheOrganization,
            city: item.backgroundInformation.city,
            state: item.backgroundInformation.state,
            localGovernmentArea: item.backgroundInformation.localGovernmentArea,
            shareholdersFund: item.backgroundInformation.shareholdersFund,
            financialInstitutionType: item.backgroundInformation.financialInstitutionType,
            operatingLicenseIssueDate: item.backgroundInformation.operatingLicenseIssueDate,
            operationCommencementDate: item.backgroundInformation.operationCommencementDate,
          }))
          this.totalCount = data.recordCount
        } else {
          this.pfiOnboardingList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadPfiOnboarding({
          search: this.filter,
          page: 1,
          limit: 100000,
          fileExportFormat: 1,
          permission: lmsPermissions.EXPORT_PFI_ONBOARDING,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
          FileSaver.saveAs(blob, 'PFI Onboarding Report.csv')
          this.alertSuccess({ text: 'Your request to download PFI Onboarding Report was successful' })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadPfiOnboarding({
          search: this.filter,
          page: 1,
          limit: 100000,
          fileExportFormat: 0,
          permission: lmsPermissions.EXPORT_PFI_ONBOARDING,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
          FileSaver.saveAs(blob, 'PFI Onboarding Report.xlsx')
          this.alertSuccess({ text: 'Your request to download PFI Onboarding Report was successful' })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async pdfExport() {
      try {
        this.isBusy = true
        const response = await this.downloadPfiOnboarding({
          search: this.filter,
          page: 1,
          limit: 100000,
          fileExportFormat: 2,
          permission: lmsPermissions.EXPORT_PFI_ONBOARDING,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/pdf' })
          FileSaver.saveAs(blob, 'PFI Onboarding Report.pdf')
          this.alertSuccess({ text: 'Your request to download PFI Onboarding Report was successful' })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    resetFilters() {
      this.filter.institutionName = ''
      this.filter.email = ''
      this.filter.currentStage = ''
      this.filter.applicationAgeInDays = ''
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchPfiOnboardingDraft()
    },
  },
}
</script>
